/**
 * Site Settings Context
 * Manages site-wide settings like Coming Soon mode and Maintenance mode
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { API_URL } from '@/lib/api';

const SiteSettingsContext = createContext(null);

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    coming_soon_mode: false,
    coming_soon_launch_date: null,
    coming_soon_tagline: '',
    maintenance_mode: false,
    maintenance_message: '',
    maintenance_estimated_time: '',
    available_regions: ['us', 'uk', 'es'],
    ads_enabled: true,
    shopify_enabled: true
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/site-settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Failed to fetch site settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const isComingSoonMode = settings.coming_soon_mode === true;
  const isMaintenanceMode = settings.maintenance_mode === true;
  const availableRegions = settings.available_regions || ['us', 'uk', 'es'];
  const isShopifyEnabled = settings.shopify_enabled !== false;
  const isAdsEnabled = settings.ads_enabled !== false;
  const appEnvironment = settings.app_environment || 'dev';

  return (
    <SiteSettingsContext.Provider value={{ 
      settings, 
      setSettings,
      loading,
      isComingSoonMode,
      isMaintenanceMode,
      availableRegions,
      isShopifyEnabled,
      isAdsEnabled,
      appEnvironment
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    return {
      settings: { coming_soon_mode: false, maintenance_mode: false, available_regions: ['us', 'uk', 'es'], shopify_enabled: true, ads_enabled: true, app_environment: 'dev' },
      loading: false,
      isComingSoonMode: false,
      isMaintenanceMode: false,
      availableRegions: ['us', 'uk', 'es'],
      isShopifyEnabled: true,
      isAdsEnabled: true,
      appEnvironment: 'dev'
    };
  }
  return context;
};

export default SiteSettingsContext;
